import { Card } from '@reflex/ui/components/ui/card';
import { Switch } from '@reflex/ui/components/ui/switch';
import { Label } from '@reflex/ui/components/ui/label';
import { PanelTitle, PageDescription } from '@reflex/ui/components/layout/Typography';

export interface OrgCreationPolicyProps {
  /** Whether ordinary users may create their own organizations (self-serve). */
  allowSelfServeOrgCreation: boolean;
  /**
   * Persists the new value. Fired immediately when the switch is toggled;
   * the caller shows success/error toasts and re-renders with the canonical
   * value from the server.
   */
  onChange: (allow: boolean) => void;
  /** Disables the control while a save is in flight. */
  isSaving: boolean;
}

/**
 * Admin control for the self-serve org-creation policy. When turned off, new
 * users can no longer create their own organizations — a platform admin must
 * provision one from the Admin → Organizations page.
 */
export function OrgCreationPolicy({
  allowSelfServeOrgCreation,
  onChange,
  isSaving,
}: OrgCreationPolicyProps) {
  return (
    <Card className="p-4 space-y-4">
      <div>
        <PanelTitle as="h2" size="sm">
          Organization creation
        </PanelTitle>
        <PageDescription className="mt-0.5 text-xs">
          Control whether people can create their own organizations when they sign up.
        </PageDescription>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-md border p-3">
        <div className="min-w-0">
          <Label htmlFor="allow-self-serve-org-creation" className="font-medium">
            Allow self-serve organization creation
          </Label>
          <p className="mt-0.5 text-xs text-muted-foreground">
            When off, new users cannot create their own organizations. Only a platform admin can
            create one from the Admin → Organizations page.
          </p>
        </div>
        <Switch
          id="allow-self-serve-org-creation"
          checked={allowSelfServeOrgCreation}
          onCheckedChange={onChange}
          disabled={isSaving}
          aria-label="Allow self-serve organization creation"
          data-testid="allow-self-serve-org-creation-toggle"
        />
      </div>
    </Card>
  );
}
